import { Link } from "@tanstack/react-router";
import { Fragment } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { importResultsPage_MultiUploadQuery } from "@app/queries/importResultsPage_MultiUploadQuery.graphql";

export const importResultsQuery = graphql`
  query importResultsPage_MultiUploadQuery($id: ID!) {
    multiUpload(id: $id) {
      id
      state
      uploads {
        id
        state
        filename
        providerKey
        accountSlug
        rowsCount
        duplicate
        notice
      }
    }
  }
`;

export default function ImportResultsPage({
  queryRef,
}: {
  queryRef: PreloadedQuery<importResultsPage_MultiUploadQuery>;
}) {
  const data = usePreloadedQuery(importResultsQuery, queryRef);
  const multiUpload = data.multiUpload;

  if (!multiUpload) {
    return <div className="p-6 text-gray-500">Import not found.</div>;
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4">
      <h1 className="text-xl font-bold">Import results</h1>

      <table className="w-full text-sm bg-white rounded border">
        <thead>
          <tr className="text-left border-b text-gray-500">
            <th className="px-3 py-2">File</th>
            <th className="px-3 py-2">Format</th>
            <th className="px-3 py-2">Account</th>
            <th className="px-3 py-2 text-right">Rows</th>
            <th className="px-3 py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {multiUpload.uploads.map((upload) => (
            <Fragment key={upload.id}>
              <tr className={upload.notice ? "" : "border-b last:border-0"}>
                <td className="px-3 py-2">{upload.filename}</td>
                <td className="px-3 py-2">{upload.providerKey ?? "—"}</td>
                <td className="px-3 py-2">{upload.accountSlug ?? "—"}</td>
                <td className="px-3 py-2 text-right">{upload.rowsCount}</td>
                <td className="px-3 py-2">
                  {upload.duplicate ? (
                    <span className="text-amber-600">
                      duplicate — already imported
                    </span>
                  ) : upload.state === "SUCCESS" ? (
                    <span className="text-green-600">imported</span>
                  ) : (
                    <span className="text-gray-500">
                      {upload.state.toLowerCase()}
                    </span>
                  )}
                </td>
              </tr>
              {/* What the import dropped or repaired. Rows a wider export window
                pulls in are not the operator's fault and not a failure, but they
                are a change to what got counted, so they are said out loud. */}
              {upload.notice && (
                <tr className="border-b last:border-0">
                  <td
                    colSpan={5}
                    className="px-3 pb-2 text-xs whitespace-pre-line text-amber-700"
                  >
                    {upload.notice}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>

      <div className="flex gap-3">
        <Link
          to="/statements/upload"
          className="text-sm text-gray-600 underline"
        >
          Upload more statements
        </Link>
        <Link to="/stakeholders" className="text-sm text-gray-600 underline">
          View stakeholders
        </Link>
      </div>
    </div>
  );
}
